import React from "react";

const INK = "#0F172A";
const BRAND = "#FF5722";
const LINE = "#CBD5E1";
const FILL_STRONG = "#475569";
const FILL_SOFT = "#E2E8F0";

function getQty(components, name) {
  return components.find((c) => c.component === name)?.quantity || 0;
}

function FrontView({ length, height, levels, panelThickness = 20, sidesPerRack }) {
  const W = 320, H = 240, PAD = 22;
  const s = Math.min((W - PAD * 2) / Math.max(length, 1), (H - PAD * 2) / Math.max(height, 1));
  const L = length * s;
  const Hg = height * s;
  const x0 = (W - L) / 2;
  const y0 = H - PAD - Hg;

  const columnW = 6;
  const beamH = 3;

  // Beam positions: evenly spaced along height (top level of each shelf)
  const beams = Array.from({ length: levels }, (_, i) => y0 + Hg - (Hg * (i + 1)) / levels);
  const panelH = Math.max(2, (Hg / levels) * 0.08);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 240 }}>
      {/* ground */}
      <line x1={PAD / 2} y1={y0 + Hg + 2} x2={W - PAD / 2} y2={y0 + Hg + 2} stroke={INK} strokeWidth="1.5" />
      {/* columns — always show full main-unit rack for clarity */}
      <rect x={x0 - columnW / 2} y={y0} width={columnW} height={Hg} fill={FILL_STRONG} />
      <rect x={x0 + L - columnW / 2} y={y0} width={columnW} height={Hg} fill={FILL_STRONG} />
      {/* beams + panels */}
      {beams.map((by, i) => (
        <g key={i}>
          <rect x={x0} y={by - beamH / 2} width={L} height={beamH} fill={BRAND} />
          <rect
            x={x0 + 2}
            y={by - beamH / 2 - panelH}
            width={L - 4}
            height={panelH}
            fill={FILL_SOFT}
            stroke={LINE}
            strokeWidth="0.5"
          />
        </g>
      ))}
      {/* dimension labels */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill={FILL_STRONG}>
        <text x={x0 + L / 2} y={y0 + Hg + 16} textAnchor="middle">length {length} mm</text>
        <text x={x0 - 10} y={y0 + Hg / 2} textAnchor="end" transform={`rotate(-90 ${x0 - 10} ${y0 + Hg / 2})`}>
          height {height} mm
        </text>
      </g>
      <text
        x={W - PAD / 2}
        y={PAD / 2 + 6}
        textAnchor="end"
        fontFamily="IBM Plex Sans, sans-serif"
        fontSize="9"
        fontWeight="700"
        letterSpacing="2"
        fill={FILL_STRONG}
      >
        FRONT · {levels}L
      </text>
    </svg>
  );
}

function SideView({ depth, height, vertsPerSide, angledPerBay }) {
  const W = 320, H = 240, PAD = 22;
  const s = Math.min((W - PAD * 2) / Math.max(depth, 1), (H - PAD * 2) / Math.max(height, 1));
  const D = depth * s;
  const Hg = height * s;
  const x0 = (W - D) / 2;
  const y0 = H - PAD - Hg;

  const columnW = 5;
  const bays = Math.max(1, vertsPerSide - 1);

  // X positions of verticals
  const xs = Array.from({ length: vertsPerSide }, (_, i) => x0 + (D * i) / bays);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 240 }}>
      {/* ground */}
      <line x1={PAD / 2} y1={y0 + Hg + 2} x2={W - PAD / 2} y2={y0 + Hg + 2} stroke={INK} strokeWidth="1.5" />

      {/* Bays with supports */}
      {xs.slice(0, -1).map((x, i) => {
        const nx = xs[i + 1];
        const topY = y0 + 2;
        const botY = y0 + Hg - 2;
        // Angled zigzag positions
        const zig = [];
        if (angledPerBay > 0) {
          const step = Hg / (angledPerBay + 1);
          for (let k = 0; k < angledPerBay; k++) {
            const yTop = y0 + step * k + 2;
            const yBot = y0 + step * (k + 1) + 2;
            const left = k % 2 === 0;
            zig.push(
              <line
                key={k}
                x1={left ? x : nx}
                y1={yTop}
                x2={left ? nx : x}
                y2={yBot}
                stroke={BRAND}
                strokeWidth="1"
              />
            );
          }
        }
        return (
          <g key={i}>
            {/* straight top + bottom */}
            <line x1={x} y1={topY} x2={nx} y2={topY} stroke={INK} strokeWidth="1.5" />
            <line x1={x} y1={botY} x2={nx} y2={botY} stroke={INK} strokeWidth="1.5" />
            {zig}
          </g>
        );
      })}

      {/* Verticals on top */}
      {xs.map((x, i) => (
        <rect key={i} x={x - columnW / 2} y={y0} width={columnW} height={Hg} fill={FILL_STRONG} />
      ))}

      {/* Base plates */}
      {xs.map((x, i) => (
        <rect key={`bp${i}`} x={x - columnW} y={y0 + Hg} width={columnW * 2} height={3} fill={INK} />
      ))}

      {/* Labels */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill={FILL_STRONG}>
        <text x={x0 + D / 2} y={y0 + Hg + 16} textAnchor="middle">depth {depth} mm · {vertsPerSide} verticals</text>
      </g>
      <text
        x={W - PAD / 2}
        y={PAD / 2 + 6}
        textAnchor="end"
        fontFamily="IBM Plex Sans, sans-serif"
        fontSize="9"
        fontWeight="700"
        letterSpacing="2"
        fill={FILL_STRONG}
      >
        SIDE · {bays} BAY{bays > 1 ? "S" : ""}
      </text>
    </svg>
  );
}

export default function RackDiagram({ config, components, sideBySide = false }) {
  if (!config || !components) return null;
  const { height, length, depth, levels, unit_type } = config;
  const sidesPerRack = unit_type === "addon" ? 1 : 2;
  const totalVerticals = getQty(components, "Vertical");
  const vertsPerSide = Math.max(2, Math.round(totalVerticals / sidesPerRack));
  // angled qty per bay = angled_total / (bays × sides)
  const angledTotal = getQty(components, "Upright Support (Angled)");
  const bays = Math.max(1, vertsPerSide - 1);
  const angledPerBay = Math.max(0, Math.round(angledTotal / (bays * sidesPerRack)));

  return (
    <div className="panel-heavy" data-testid="rack-diagram">
      <div className="px-6 py-3 border-b-2 border-ink flex items-center justify-between">
        <div className="label-tech">Blueprint · Visual</div>
        <div className="mono text-xs text-ink-muted">
          {unit_type === "addon" ? "Add-on unit" : "Main unit"}
        </div>
      </div>
      <div className={sideBySide ? "grid grid-cols-2" : "grid grid-cols-1 md:grid-cols-2"}>
        <div className={
          sideBySide
            ? "p-4 border-r border-line bp-grid"
            : "p-4 border-b md:border-b-0 md:border-r border-line bp-grid"
        }>
          <FrontView
            length={length}
            height={height}
            levels={levels}
            sidesPerRack={sidesPerRack}
          />
        </div>
        <div className="p-4 bp-grid">
          <SideView
            depth={depth}
            height={height}
            vertsPerSide={vertsPerSide}
            angledPerBay={angledPerBay}
          />
        </div>
      </div>
    </div>
  );
}
